import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { Uploader } from '../../../../components/Uploader';
import { FileTypes } from '../../../../types/Files/FileTypes';
// import { Header } from '../../../../components/header/Header';
import { NextSection } from '../../../../components/buttons/NextSection';
import { NavForward } from '../../../../components/buttons/NavForward';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { isMobile } from 'react-device-detect';
import { Button } from '../../../../components/buttons/Button';
import * as Sentry from '@sentry/browser';
import { Heading } from '../../../../components/Heading';
import Checkbox from '../../../../components/forms/Checkbox';

export const Route = createFileRoute('/_authenticated/$patientId/upload/files')({
  component: FileUploads,
});

function FileUploads() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [organDonorStatus, setOrganDonorStatus] = React.useState('');
  const [hasLivingWill, setHasLivingWill] = React.useState<boolean | undefined>(false);
  const [hasPOLST, setHasPOLST] = React.useState<boolean | undefined>(false);
  const [hasPowerOfAttorney, setHasPowerOfAttorney] = React.useState<boolean | undefined>(false);
  const [noDocs, setNoDocs] = React.useState(false);
  const { mutate } = useUpdateChartSection();
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [warned, setWarned] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // WRITE FORM DATA TO FIRESTORE
  const handleLivingWillInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: hasLivingWill === undefined ? false : hasLivingWill,
            organDonor: organDonorStatus,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE ORGAN DONOR RADIO BUTTON CLICKS
  const handleOrganDonorStatusChange = (status: string) => {
    setOrganDonorStatus(status);
    mutate(
      {
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: hasLivingWill === undefined ? false : hasLivingWill,
            organDonor: status,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE LIVING WILL CHECKBOX CLICKS
  const handleLivingWillStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            livingWillExists: !hasLivingWill,
            livingWillURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE POLST CHECKBOX CLICKS
  const handlePOLSTStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            POLSTExists: !hasPOLST,
            POLSTURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE POWER OF ATTORNEY CHECKBOX CLICKS
  const handlePowerOfAttorneyStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            powerOfAttorneyExists: !hasPowerOfAttorney,
            powerOfAttorneyURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // SET POWER OF ATTORNEY STATUS FROM FIRESTORE
  React.useEffect(() => {
    if (chart?.LIVING_WILL?.organDonor) {
      setOrganDonorStatus(chart.LIVING_WILL.organDonor);
    }

    setHasLivingWill(chart?.OTHER_DOCUMENTS?.livingWillExists === true ? true : false);
    setHasPOLST(chart?.OTHER_DOCUMENTS?.POLSTExists === true ? true : false);
    setHasPowerOfAttorney(
      chart?.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney === 'yes'
        ? true
        : chart?.OTHER_DOCUMENTS?.powerOfAttorneyExists === true
          ? true
          : chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI && chart.OTHER_DOCUMENTS.powerOfAttorneyURI.length > 0
            ? true
            : false,
    );
  }, [chart]);

  // INITIALIZE OTHER DOCUMENTS DOCUMENT
  const handleOtherDocumentsInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {},
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FIRESTORE DOCS IF THEY DO NOT EXIST
  React.useEffect(() => {
    const livingWillDocRef = chart?.LIVING_WILL;
    if (chart && !livingWillDocRef) {
      handleLivingWillInfoUpdate();
    }
    if (chart?.LIVING_WILL?.organDonor) {
      setOrganDonorStatus(chart.LIVING_WILL.organDonor);
    }
    const otherDocumentsDocRef = chart?.OTHER_DOCUMENTS;
    if (chart && !otherDocumentsDocRef) {
      handleOtherDocumentsInfoUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'files', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'files',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="directives-upload-page" className="flex w-full flex-col items-center justify-center">
          <Heading id="directives-heading" textContent="Legal Documents" />
          <div className="w-8/12">
            <details className="mb-1 w-full border-2 border-stone-500 bg-slate-200 p-4 open:bg-slate-50 open:[&_svg]:-rotate-180">
              <summary className="flex cursor-pointer list-none items-center gap-4 text-xl">
                <div className="">
                  <svg
                    className="rotate-0 transform text-blue-700 transition-all duration-300"
                    fill="none"
                    height="20"
                    width="20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                <div>Why should I upload my legal documents?</div>
              </summary>
              <p className="text-xl font-bold">Every MIDEO card is printed with two QR codes.</p>
              <p className="text-xl">
                - The QR code on the front is for Emergency personnel and links to the MIDEO safety statement you will
                record today.
              </p>
              <p className="text-xl">
                - The QR code on the reverse links to a paper record of your MIDEO. Uploading documents makes them part
                of this record and therefore instantly available wherever and whenever they are needed.
              </p>
              <p className="text-xl">
                * If you have documents but are not prepared to upload them now, you may check the appropriate box(es)
                and continue with the application. You (or your representative) may then email them to{' '}
                <button
                  className="text-blue-500 underline"
                  onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
                >
                  info@mideohealth.com
                </button>{' '}
                any time after the application is submitted.
              </p>
              <p className="text-xl">
                **Additional documents (living will and/or medical power of attorney) are not required, but it is
                encouraged that you include them if you have them or are in the process of creating them already.{' '}
              </p>
              {/* <p className="text-lg italic">
                CAUTION: The front of your card will indicate the existence of these documents. You should carry them on
                your person until the time at which they are uploaded and part of your record. Failing to do so, will
                likely cause confusion for those involved in your care
              </p> */}
            </details>
          </div>
          <form>
            {patientInfo.navType.base !== 'self' && (
              <fieldset>
                <legend className="text-2xl font-bold">Decision Regarding Organ Donation:</legend>
                <div>
                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="organDonor"
                      name="organDonorStatus"
                      value="Yes"
                      checked={organDonorStatus === 'Yes'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="organDonor">
                      I am an organ donor
                    </label>
                  </div>

                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="notOrganDonor"
                      name="organDonorStatus"
                      value="No"
                      checked={organDonorStatus === 'No'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2" htmlFor="notOrganDonor">
                      I am not an organ donor
                    </label>
                  </div>

                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="TBD"
                      name="organDonorStatus"
                      value="TBD"
                      checked={organDonorStatus === 'TBD'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="TBD">
                      My organ donation status is undecided
                    </label>
                  </div>
                </div>
              </fieldset>
            )}
            {/* <h2 className="mt-2 text-2xl font-bold">Legal Documents:</h2> */}
            <div className="mt-6">
              <Checkbox
                id="livingWill"
                name="livingWill"
                checked={hasLivingWill === undefined ? false : hasLivingWill}
                onChange={handleLivingWillStatusChange}
                label="I have a living will"
              />
            </div>
            {hasLivingWill ? (
              chart?.OTHER_DOCUMENTS?.livingWillURI ? (
                <>
                  <a
                    className="text-xl font-medium text-blue-600 hover:underline dark:text-blue-500"
                    href={chart?.OTHER_DOCUMENTS?.livingWillURI}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <p className="text-xl">View current living will</p>
                  </a>
                </>
              ) : (
                <>
                  <p className="text-xl">Upload a living will:</p>
                  <Uploader
                    docToUpdate="OTHER_DOCUMENTS"
                    bucket="public"
                    type={FileTypes.livingWill}
                    title="Upload Living Will"
                    elementId="livingWill-file-upload"
                  />
                </>
              )
            ) : null}
            {patientInfo.navType.base !== 'self' && (
              <div>
                <Checkbox
                  id="hasPOLST"
                  name="POLST"
                  checked={hasPOLST === undefined ? false : hasPOLST}
                  onChange={handlePOLSTStatusChange}
                  label="I have a POLST"
                />
                {hasPOLST ? (
                  chart?.OTHER_DOCUMENTS?.POLSTURI ? (
                    <>
                      <a
                        className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                        href={chart?.OTHER_DOCUMENTS?.POLSTURI}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <p className="text-xl">View current POLST</p>
                      </a>
                    </>
                  ) : (
                    <>
                      <p className="text-xl">Upload a POLST:</p>
                      <Uploader
                        docToUpdate="OTHER_DOCUMENTS"
                        bucket="public"
                        type={FileTypes.POLST}
                        title="Upload POLST"
                        elementId="POLST-file-upload"
                      />
                    </>
                  )
                ) : null}
              </div>
            )}
            <div>
              <Checkbox
                id="hasPowerOfAttorney"
                name="powerOfAttorney"
                checked={hasPowerOfAttorney ? hasPowerOfAttorney : false}
                onChange={handlePowerOfAttorneyStatusChange}
                label="I have a medical durable power of attorney"
              />
              {hasPowerOfAttorney ? (
                chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI ? (
                  <>
                    <a
                      className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                      href={chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <p className="text-xl">View current medical durable power of attorney</p>
                    </a>
                  </>
                ) : (
                  <>
                    <p className="text-xl">Upload a medical durable power of attorney:</p>
                    <Uploader
                      docToUpdate="OTHER_DOCUMENTS"
                      bucket="public"
                      type={FileTypes.powerOfAttorney}
                      title="Upload Medical Durable Power of Attorney"
                      elementId="powerOfAttorney-file-upload"
                    />
                  </>
                )
              ) : null}
            </div>
            {hasLivingWill === false && hasPOLST === false && hasPowerOfAttorney === false && (
              <div>
                <Checkbox
                  id="noDocs"
                  name="noDocs"
                  checked={noDocs}
                  onChange={() => setNoDocs(!noDocs)}
                  label="I don’t have any of the above documents"
                />
              </div>
            )}
          </form>

          <div id="nav" className="flex flex-col items-center">
            <Button
              id="submit-info"
              textContent="Submit"
              type="button"
              onClick={() => setIsSubmitted(true)}
              submitted={isSubmitted}
            />
            {patientInfo.navType.base === 'self' && nextPageURL && isSubmitted && (
              <NavForward to={nextPageURL} disabled={organDonorStatus.length === 0} />
            )}
            {patientInfo?.conciergeNavigator?.educationUnlocked && (
              <div className="place-self-center">
                {nextPageURL && organDonorStatus?.length > 0 && isSubmitted && <NextSection to={nextPageURL} />}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
  return <div>Loading...</div>;
}
